import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import { QueryProps, type BaseApiResponse } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  CreateEvent,
  EventTicketTier,
  EventsResponse,
  EventStatsResponse,
  EventTicketsResponse,
  FeaturedEventsResponse,
  FeatureEventPayload,
  TicketStatus,
  TicketValidationResponse,
} from './events.type'

export const productsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    saveEventTier: builder.mutation<
      BaseApiResponse & { data: EventTicketTier },
      { eventId: string; tier: EventTicketTier }
    >({
      query: ({ eventId, tier: { _id, ...payload } }) => ({
        url: `v2/events/${eventId}/tiers${_id ? `/${_id}` : ''}`,
        method: _id ? 'PUT' : 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EVENT_TICKET', id: 'LIST' },
        { type: 'EVENT', id: arg.eventId },
        'EVENT_TICKET',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data.success) toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    createEvent: builder.mutation<BaseApiResponse, CreateEvent>({
      query: (payload) => ({
        url: `v1/events/create`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'EVENT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    editEvent: builder.mutation<
      BaseApiResponse,
      {
        id: string
        payload: CreateEvent
      }
    >({
      query: ({ id, payload }) => ({
        url: `v1/events/edit/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EVENT', id: arg.id },
        { type: 'EVENT', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getEventTickets: builder.query<
      EventTicketsResponse,
      QueryProps & {
        id: string
        status?: TicketStatus
      }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `v1/events/${id}`,
          method: 'GET',
          params: { ...params },
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'EVENT_TICKET', id: 'LIST' },
        { type: 'EVENT', id: arg.id },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          console.log(err)
        }
      },
    }),

    getEventStats: builder.query<
      EventStatsResponse,
      {
        id: string
      }
    >({
      query: ({ id, ...params }) => {
        return {
          url: `v1/events/${id}/stats`,
          method: 'GET',
          params: { ...params },
        }
      },
      providesTags: ['EVENT_TICKET'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err) {
          console.log(err)
        }
      },
    }),

    getAllEvents: builder.query<EventsResponse, QueryProps>({
      query: (args) => {
        return {
          url: `v1/events`,
          method: 'GET',
          params: {
            isAdmin: true,
            limit: args.limit,
            skip: args.skip,
            search: args.search,
            ...args,
          },
        }
      },
      providesTags: [{ type: 'EVENT', id: 'LIST' }],
    }),

    checkInTicket: builder.mutation<
      BaseApiResponse,
      { eventId: string; ticketCode: string }
    >({
      query: ({ eventId, ticketCode }) => ({
        url: `v1/events/${eventId}/${ticketCode}/check-in`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EVENT_TICKET', id: 'LIST' },
        { type: 'EVENT', id: arg.eventId },
        'EVENT_TICKET',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    validateTicketCode: builder.mutation<
      TicketValidationResponse,
      { ticketCode: string }
    >({
      query: ({ ticketCode }) => ({
        url: `v1/events/validate/${ticketCode}`,
        method: 'GET',
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getFeaturedEvents: builder.query<FeaturedEventsResponse, void>({
      query: () => ({ url: `v2/events/featured`, method: 'GET' }),
      providesTags: [{ type: 'EVENT', id: 'FEATURED' }],
    }),

    featureEvent: builder.mutation<BaseApiResponse, FeatureEventPayload>({
      query: (payload) => ({
        url: `v2/events/featured`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'EVENT', id: 'FEATURED' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    unfeatureEvent: builder.mutation<
      BaseApiResponse,
      { fulfilmentCentreId?: string }
    >({
      query: (params) => ({
        url: `v2/events/featured`,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: [{ type: 'EVENT', id: 'FEATURED' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useSaveEventTierMutation,
  useCreateEventMutation,
  useEditEventMutation,
  useGetEventTicketsQuery,
  useGetEventStatsQuery,
  useLazyGetEventStatsQuery,
  useGetAllEventsQuery,
  useCheckInTicketMutation,
  useValidateTicketCodeMutation,
  useGetFeaturedEventsQuery,
  useFeatureEventMutation,
  useUnfeatureEventMutation,
} = productsApiSlice
